// A copy of the Sandbox tutorial frame (DashboardAlt/Components/SandboxTutorial.tsx), without its "seen" flag.
// Merge the two frames into one shared component when both need the same change.
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Button, Modal, Space, Steps, Typography } from "antd";
import img_overview from "./img/notes-overview.png";
import img_dialog from "./img/notes-dialog.png";
import img_tags from "./img/notes-tags.png";
import img_attributes from "./img/notes-attributes.png";
import img_visibility from "./img/notes-visibility.png";
import img_filters from "./img/notes-filters.png";
import img_tableView from "./img/notes-table-view.png";
import img_settings from "./img/notes-settings.png";

interface NotesTutorialProps {
    isOpen: boolean;
    setOpen: (open: boolean) => void;
}

// The content pane is min(710px, 100vh - 170px) tall. A step with a one-line paragraph has about 120px of text and margins.
const FIT_UNDER_SHORT_TEXT = "min(590px, calc(100vh - 300px))";

// `maxHeight` keeps a tall image inside the pane, so the step does not scroll. The width then follows the height.
const Shot = ({ src, alt, maxWidth = "100%", maxHeight }: { src: string; alt: string; maxWidth?: number | string; maxHeight?: string }) => (
    <img className="notes-tutorial-image" src={src} alt={alt} style={maxHeight ? { maxHeight, width: "auto", maxWidth: "100%" } : { maxWidth }} />
);

const STEPS: { title: string; description: string; content: ReactNode }[] = [
    {
        title: "Welcome",
        description: "Staff notes about your players.",
        content: (
            <>
                <p>
                    Notes keep what your staff sees about each player in one place: practice, games, recovery, and progress. Every note
                    is about one player. Players can't see notes.
                </p>
                <Shot src={img_overview} alt="The Notes tab with filters at the top and a list of notes about John Doe" />
            </>
        ),
    },
    {
        title: "Write a Note",
        description: "Player, text, tags, and more.",
        content: (
            <>
                <p>
                    Click <b>+ New note</b>. Choose the player and write the note. Tags, attributes, and visibility are optional. Then
                    click <b>Save note</b>.
                </p>
                <Shot
                    src={img_dialog}
                    alt="The New note dialog with a player, the note text, a tag, a Score of 8, and Open visibility"
                    maxHeight={FIT_UNDER_SHORT_TEXT}
                />
            </>
        ),
    },
    {
        title: "Tags and Attributes",
        description: "Group notes and track numbers.",
        content: (
            <>
                <p>
                    A <b>tag</b> is a short label that groups notes, such as Game or Recovery. Choose a tag from the list, or type a new
                    tag and press Enter. A new tag is shared with your whole team.
                </p>
                <Shot src={img_tags} alt="The Tags field with a new tag typed and the hint Press Enter to create this tag" maxWidth={620} />
                <p className="notes-tutorial-section">
                    An <b>attribute</b> is a number on a note, such as a Score from 1 to 10. Click <b>+ Add attribute</b>, choose the
                    attribute, and enter a value inside its range.
                </p>
                <Shot src={img_attributes} alt="The Attributes field with Score chosen, a value of 8, and the range 1 to 10" maxWidth={620} />
            </>
        ),
    },
    {
        title: "Who Can Read It",
        description: "Open or targeted.",
        content: (
            <>
                <ul className="notes-tutorial-list">
                    <li>
                        <b>Open</b>: everyone on the staff who can view notes can read the note.
                    </li>
                    <li>
                        <b>Targeted</b>: only the people that you add can read the note.
                    </li>
                </ul>
                <p>You can always see your own notes. Players never see notes.</p>
                <Shot src={img_visibility} alt="The Visibility field set to Targeted with three people added" maxWidth={620} />
            </>
        ),
    },
    {
        title: "Find and View Notes",
        description: "Filters, list, and table.",
        content: (
            <>
                <p>
                    Use the filters at the top of the tab: dates, text search, player, tag, author, and attribute. A note must match
                    every filter.
                </p>
                <Shot src={img_filters} alt="The filter bar with the dates, a text search, and John Doe in the player filter" />
                <p className="notes-tutorial-section">
                    Use the three buttons above the notes to show them as a list, in two columns, or as a table.
                </p>
                <p>
                    In the <b>Table View</b>, click <b>Columns</b> to choose the tags for each column and to highlight an attribute. Save
                    your columns as a preset to use them again.
                </p>
                <Shot src={img_tableView} alt="The table view with the columns All Notes, Games, and Progress, and Score values highlighted" />
            </>
        ),
    },
    {
        title: "Attributes + Tags",
        description: "Manage the lists of your team.",
        content: (
            <>
                <p>
                    Users with the appropriate permissions can manage the attributes and tags of the whole team here. An attribute or a
                    tag can be deleted only when no note uses it.
                </p>
                <Shot src={img_settings} alt="The Attributes + Tags page with the note attributes and the note tags" />
            </>
        ),
    },
    {
        title: "You're All Set",
        description: "Start writing notes.",
        content: (
            <>
                <p>
                    Open this tutorial again at any time with the <b>Show Tutorial</b> button.
                </p>
                <p>
                    Need help? Reach out to our support team anytime at <b>cs@whistleperformance.ai</b>.
                </p>
            </>
        ),
    },
];

export const NotesTutorial = ({ isOpen, setOpen }: NotesTutorialProps) => {
    const [current, setCurrent] = useState(0);
    const contentRef = useRef<HTMLDivElement>(null);

    // A new step starts at the top of the scrollable content pane.
    useEffect(() => {
        contentRef.current?.scrollTo({ top: 0 });
    }, [current]);

    const isLast = current === STEPS.length - 1;

    const handleClose = () => {
        setOpen(false);
        setCurrent(0);
    };

    return (
        <Modal
            className="notes-tutorial-modal"
            title="Notes Tutorial"
            open={isOpen}
            onCancel={handleClose}
            width={1200}
            centered
            footer={
                <Space>
                    {current > 0 && (
                        <Button onClick={() => setCurrent(current - 1)} data-attr="notes.notes-tutorial.previous">
                            Previous
                        </Button>
                    )}
                    {isLast ? (
                        <Button type="primary" onClick={handleClose} data-attr="notes.notes-tutorial.close">
                            Done
                        </Button>
                    ) : (
                        <Button type="primary" onClick={() => setCurrent(current + 1)} data-attr="notes.notes-tutorial.next">
                            Next
                        </Button>
                    )}
                </Space>
            }
        >
            <div className="notes-tutorial-body">
                <div className="notes-tutorial-steps">
                    <Steps
                        current={current}
                        onChange={setCurrent}
                        items={STEPS.map(({ title, description }) => ({ title, description }))}
                        direction="vertical"
                        size="small"
                    />
                </div>
                <div className="notes-tutorial-content" ref={contentRef}>
                    <Typography.Title level={3}>{STEPS[current].title}</Typography.Title>
                    <div className="notes-tutorial-prose">{STEPS[current].content}</div>
                </div>
            </div>
        </Modal>
    );
};
